import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Blueprint, Button, Field, Input } from '@/components/ui';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { AuthError, login, SignupError, signup } from '@/lib/auth';
import { detectCapsLock } from '@/lib/keyboard';
import { scorePasswordStrength, type PasswordStrengthLevel } from '@/lib/password_strength';
import { useSessionStore } from '@/stores/session';

const SUCCESS_REDIRECT = '/onboarding';
const PASSWORD_MIN = 8;

function mapSignupError(err: unknown): string {
  if (err instanceof SignupError) {
    if (err.reason === 'email_already_registered') return ptBR.errors.emailAlreadyRegistered;
    if (err.reason === 'network') return ptBR.errors.networkOffline;
    if (err.reason === 'validation') return ptBR.errors.validation;
    return ptBR.errors.generic;
  }
  if (err instanceof AuthError) {
    return err.reason === 'network' ? ptBR.errors.networkOffline : ptBR.errors.generic;
  }
  return ptBR.errors.generic;
}

const STRENGTH_LABEL: Record<PasswordStrengthLevel, string> = {
  weak: ptBR.signup.strengthWeak,
  medium: ptBR.signup.strengthMedium,
  strong: ptBR.signup.strengthStrong,
  veryStrong: ptBR.signup.strengthVeryStrong,
};

const STRENGTH_CLASS: Record<PasswordStrengthLevel, string> = {
  weak: 'bg-danger-500',
  medium: 'bg-warning-500',
  strong: 'bg-success-500',
  veryStrong: 'bg-success-700',
};

function StrengthMeter({ level, percent }: { level: PasswordStrengthLevel; percent: number }) {
  return (
    <div
      id="signup-password-strength"
      data-testid="signup-password-strength"
      data-level={level}
      className="mt-1 flex items-center gap-3"
    >
      <div className="h-1.5 flex-1 overflow-hidden bg-surface-3">
        <div
          className={`h-full transition-all duration-base ease-sc ${STRENGTH_CLASS[level]}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span
        className="font-mono text-caption uppercase tracking-[0.02em] text-fg-dim"
        data-testid="signup-password-strength-label"
      >
        {ptBR.signup.strengthLabel}: {STRENGTH_LABEL[level]}
      </span>
    </div>
  );
}

interface PasswordFieldProps {
  password: string;
  onPasswordChange: (next: string) => void;
  showPassword: boolean;
  onToggleShow: () => void;
  capsLock: boolean;
  onCapsLockChange: (next: boolean) => void;
  submitting: boolean;
  strength: { level: PasswordStrengthLevel; percent: number };
}

function PasswordField({
  password,
  onPasswordChange,
  showPassword,
  onToggleShow,
  capsLock,
  onCapsLockChange,
  submitting,
  strength,
}: PasswordFieldProps) {
  const inputType = showPassword ? 'text' : 'password';
  const toggleLabel = showPassword ? ptBR.signup.passwordHide : ptBR.signup.passwordShow;
  return (
    <Field label={ptBR.signup.passwordLabel} htmlFor="signup-password-input">
      <div className="relative flex">
        <Input
          id="signup-password-input"
          type={inputType}
          required
          minLength={PASSWORD_MIN}
          autoComplete="new-password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          onKeyUp={(e) => onCapsLockChange(detectCapsLock(e))}
          onKeyDown={(e) => onCapsLockChange(detectCapsLock(e))}
          onBlur={() => onCapsLockChange(false)}
          placeholder={ptBR.signup.passwordPlaceholder}
          disabled={submitting}
          aria-describedby="signup-password-hint signup-password-strength"
          className="pr-10"
          data-testid="signup-password"
        />
        <button
          type="button"
          onClick={onToggleShow}
          aria-label={toggleLabel}
          aria-pressed={showPassword}
          data-testid="signup-password-toggle"
          className="absolute inset-y-0 right-2 grid w-7 place-items-center text-fg-dim transition-colors duration-fast ease-sc hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
        >
          {showPassword ? (
            <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
          ) : (
            <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
          )}
        </button>
      </div>
      <span id="signup-password-hint" className="mt-1 text-caption text-fg-dim">
        {ptBR.signup.passwordHint}
      </span>
      {password.length > 0 ? (
        <StrengthMeter level={strength.level} percent={strength.percent} />
      ) : null}
      {capsLock ? (
        <p
          data-testid="signup-caps-warning"
          role="status"
          className="mt-1 text-caption text-warning-700"
        >
          {ptBR.signup.capsLockOn}
        </p>
      ) : null}
    </Field>
  );
}

function PasswordConfirmField({
  value,
  onChange,
  showPassword,
  submitting,
  mismatch,
}: {
  value: string;
  onChange: (next: string) => void;
  showPassword: boolean;
  submitting: boolean;
  mismatch: boolean;
}) {
  const inputType = showPassword ? 'text' : 'password';
  return (
    <Field label={ptBR.signup.passwordConfirmLabel} htmlFor="signup-password-confirm">
      <Input
        id="signup-password-confirm"
        type={inputType}
        required
        minLength={PASSWORD_MIN}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={ptBR.signup.passwordConfirmPlaceholder}
        disabled={submitting}
        aria-invalid={mismatch}
        aria-describedby={mismatch ? 'signup-password-mismatch' : undefined}
        className="aria-[invalid=true]:border-danger-500"
        data-testid="signup-password-confirm"
      />
      {mismatch ? (
        <p
          id="signup-password-mismatch"
          role="alert"
          data-testid="signup-password-mismatch"
          className="mt-1 text-caption text-danger-700"
        >
          {ptBR.signup.passwordMismatch}
        </p>
      ) : null}
    </Field>
  );
}

function LgpdSection({
  accepted,
  onToggle,
  onOpenModal,
}: {
  accepted: boolean;
  onToggle: (next: boolean) => void;
  onOpenModal: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      {/*
        Segue caixa de seleção, e não o `.radio` do pacote: aceitar o tratamento
        de dados é um consentimento que se dá ou não se dá — não é escolha
        exclusiva entre opções. O que muda é a forma (canto reto, aço), não o
        controle.
      */}
      <label className="flex items-start gap-2 text-body-sm text-fg">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onToggle(e.target.checked)}
          required
          data-testid="signup-lgpd-checkbox"
          className="mt-1 h-4 w-4 border-border accent-accent-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
        <span>{ptBR.signup.lgpdLabel}</span>
      </label>
      <button
        type="button"
        onClick={onOpenModal}
        data-testid="signup-lgpd-read-cta"
        className="shrink-0 text-body-sm text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
      >
        {ptBR.signup.lgpdReadCta}
      </button>
    </div>
  );
}

export function SignupPage() {
  const apiClient = useApiClient();
  const setToken = useSessionStore((s) => s.setToken);
  const navigate = useNavigate();
  const nomeRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [nomeEscritorio, setNomeEscritorio] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [acceptLgpd, setAcceptLgpd] = useState(false);
  const [lgpdOpen, setLgpdOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    nomeRef.current?.focus();
  }, []);

  const strength = useMemo(() => scorePasswordStrength(password), [password]);
  const mismatch = passwordConfirm.length > 0 && passwordConfirm !== password;
  const lgpdMissing = !acceptLgpd;
  const canSubmit = !submitting && password.length >= PASSWORD_MIN && !mismatch && acceptLgpd;

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setErrorMessage(null);
    if (mismatch) {
      setErrorMessage(ptBR.signup.passwordMismatch);
      return;
    }
    if (lgpdMissing) {
      setErrorMessage(ptBR.signup.lgpdRequired);
      return;
    }
    setSubmitting(true);
    try {
      await signup(apiClient, email, password, nomeEscritorio);
      const loginResp = await login(apiClient, email, password);
      setToken(loginResp.access_token);
      navigate(SUCCESS_REDIRECT, { replace: true });
    } catch (err) {
      setErrorMessage(mapSignupError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Blueprint
      as="section"
      data-testid="signup-page"
      aria-labelledby="signup-title"
      className="bg-bg px-8 py-[34px]"
    >
      <p className="mb-2.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
        {ptBR.authShell.accessKicker}
      </p>
      <h1 id="signup-title" className="font-display text-[32px] leading-none text-fg">
        {ptBR.signup.pageTitle}
      </h1>
      <p className="mt-1.5 text-body-sm text-fg-muted">{ptBR.signup.intro}</p>

      <form
        onSubmit={handleSubmit}
        aria-describedby={errorMessage ? 'signup-error' : undefined}
        className="mt-[26px] flex flex-col gap-4"
      >
        <Field label={ptBR.signup.nomeEscritorioLabel} htmlFor="signup-nome-escritorio-input">
          <Input
            ref={nomeRef}
            id="signup-nome-escritorio-input"
            type="text"
            required
            value={nomeEscritorio}
            onChange={(e) => setNomeEscritorio(e.target.value)}
            placeholder={ptBR.signup.nomeEscritorioPlaceholder}
            disabled={submitting}
            data-testid="signup-nome-escritorio"
          />
        </Field>

        <Field label={ptBR.signup.emailLabel} htmlFor="signup-email-input">
          <Input
            id="signup-email-input"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={ptBR.signup.emailPlaceholder}
            disabled={submitting}
            data-testid="signup-email"
          />
        </Field>

        <PasswordField
          password={password}
          onPasswordChange={setPassword}
          showPassword={showPassword}
          onToggleShow={() => setShowPassword((s) => !s)}
          capsLock={capsLock}
          onCapsLockChange={setCapsLock}
          submitting={submitting}
          strength={strength}
        />

        <PasswordConfirmField
          value={passwordConfirm}
          onChange={setPasswordConfirm}
          showPassword={showPassword}
          submitting={submitting}
          mismatch={mismatch}
        />

        <LgpdSection
          accepted={acceptLgpd}
          onToggle={setAcceptLgpd}
          onOpenModal={() => setLgpdOpen(true)}
        />

        {errorMessage ? (
          <p
            id="signup-error"
            role="alert"
            data-testid="signup-error"
            className="border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          >
            {errorMessage}
          </p>
        ) : null}

        <Button
          type="submit"
          size="block"
          disabled={!canSubmit}
          className="mt-1.5 tracking-[0.04em]"
          data-testid="signup-submit"
        >
          {submitting ? ptBR.signup.submitting : ptBR.signup.submit}
        </Button>
      </form>

      <div className="mt-[26px] flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4 text-[12.5px] text-fg-dim">
        <span>
          {ptBR.signup.hasAccount}{' '}
          <Link
            to="/login"
            className="text-accent-700 underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-accent"
          >
            {ptBR.signup.loginCta}
          </Link>
        </span>
        <span className="font-mono text-[11.5px]">
          {ptBR.login.footerVersion} · {ptBR.login.footerLgpd} · {ptBR.login.footerSupport}
        </span>
      </div>

      {lgpdOpen ? (
        <ConfirmModal
          open
          onOpenChange={(open) => {
            if (!open) setLgpdOpen(false);
          }}
          title={ptBR.signup.lgpdSoonTitle}
          description={ptBR.signup.lgpdSoonBody}
          confirmLabel={ptBR.signup.lgpdSoonConfirm}
          cancelLabel={ptBR.common.close}
          onConfirm={() => setLgpdOpen(false)}
        />
      ) : null}
    </Blueprint>
  );
}
